import Grid2 from '@mui/material/Unstable_Grid2';
import Cookies from 'js-cookie';
import { Fragment, useState } from 'react';

import Eyebrow from '@/components/Eyebrow/Eyebrow';
import LocationDisplay from '@/components/GlobalNavigation/Location/LocationDisplay/LocationDisplay';
import Icon, { IconName } from '@/components/Icon/Icon';
import GridContainer2 from '@/components/Layout/GridContainer2/GridContainer2';
import Link from '@/components/Link/Link';
import { getLoginUrlByEnv, MYSUNRUN_URL } from '@/constants/baseUrls';
import { ContentStyle, type GlobalFooterContent } from '@/contentful';
import type { userLocationInterface } from '@/hooks/geo';
import PrivacyOptions from '@/public/cmsAssets/assets/img/icons/PrivacyOptions';
import { Environment } from '@/utils/environment';

import useStyles from './GlobalFooter.styles';
import SolarByState from './SolarByState/SolarByState';

export interface GlobalFooterProps extends userLocationInterface {
  content: GlobalFooterContent;
}

export default function GlobalFooter({
  content: {
    fields: {
      style,
      t1Menu,
      linkColumns,
      solarByStateMenu,
      socialIconLinks,
      copyrightText,
      legalLinks,
    },
  },
  userLocation,
  setUserLocation,
}: GlobalFooterProps) {
  const isOnDark = style === ContentStyle.OnDark;
  const [solarByStateOpen, setSolarByStateOpen] = useState<boolean>(false);
  const { classes, cx } = useStyles({ isOnDark });

  // biome-ignore lint/correctness/noNestedComponentDefinitions: Automated migration to Biome
  const Socials = () => (
    <div className={classes.socialIcons}>
      {socialIconLinks.map(
        ({
          fields: {
            url,
            icon: {
              fields: { name },
            },
          },
        }) => (
          <Link
            href={url}
            key={name}
            className={classes.linkColor}
            aria-label={
              name ? `Footer ${name} link` : 'Footer Social Media link'
            }
            noPrefetchOnHover
          >
            <Icon name={name} />
          </Link>
        )
      )}
    </div>
  );

  const clearConsentCookiesAndReload = () => {
    // Clear all cookies related to consent
    const consentCookies = [
      'sunrun_allow_analytics_storage',
      'sunrun_allow_add_storage',
      'sunrun_cookie_set',
    ];

    consentCookies.forEach((cookieName) => {
      Cookies.remove(cookieName);
    });

    // Reload the page to trigger the cookie consent banner
    window.location.reload();
  };

  return (
    <footer className={classes.container}>
      <GridContainer2>
        {/* T1 */}
        <Grid2 xs={12} md={3} className={classes.t1}>
          {t1Menu.fields.links?.map(({ fields: { text, url } }) => (
            <Link
              className={cx(classes.t1Row, classes.linkColor)}
              key={text}
              href={url}
              noPrefetchOnHover
            >
              <div className={classes.t1Item}>
                <p>{text}</p>
                {!url.startsWith('/') && (
                  <Icon
                    name={IconName.EXTERNAL_LINK}
                    className={classes.externalLinkIcon}
                  />
                )}
              </div>
              <Icon
                name={IconName.ARROW_RIGHT}
                className={classes.t1HoverIcon}
              />
            </Link>
          ))}
        </Grid2>

        {/* Link Columns */}
        {linkColumns.map(({ fields: { name, links } }, index) => (
          <Grid2 xs={12} sm={4} md={2} key={name}>
            <div
              className={cx(
                classes.linkColumn,
                index === 0 && classes.firstLinkColumn
              )}
            >
              <Eyebrow
                text={name || ''}
                isOnDark={isOnDark}
                className='linkColumnEyebrow'
              />
              {links?.map(({ fields: { text, url } }) => {
                const verifiedUrl =
                  url === MYSUNRUN_URL[Environment.PROD]
                    ? getLoginUrlByEnv()
                    : url;
                return (
                  <div
                    className={cx(classes.linkColumnItem, classes.linkColor)}
                    key={text}
                  >
                    <Link href={verifiedUrl} noPrefetchOnHover>
                      {text}
                    </Link>
                  </div>
                );
              })}
              {index === 0 && (
                <SolarByState
                  isOnDark={isOnDark}
                  solarByStateMenu={solarByStateMenu}
                  solarByStateOpen={solarByStateOpen}
                  setSolarByStateOpen={setSolarByStateOpen}
                />
              )}
            </div>
          </Grid2>
        ))}

        {/* Socials Md+ */}
        <Grid2 md={3} className={classes.socialIconsLargeContainer}>
          <Socials />
        </Grid2>
      </GridContainer2>

      {/* Lower Content */}
      <GridContainer2 gridProps={{ className: classes.lowerContent }}>
        <Grid2 xs={12}>
          <div
            className={cx('be-ix-link-block', classes.popularPagesList)}
          ></div>
        </Grid2>
        <Grid2 xs={12} className={classes.primaryLowerContent}>
          <div className={classes.stateAndCopy}>
            <LocationDisplay
              userLocation={userLocation}
              setUserLocation={setUserLocation}
              className={classes.stateSelector}
              isOnDark={isOnDark}
            />
            <div className={classes.copyright}>{copyrightText}</div>
          </div>
          <div className={classes.lowerContentSocials}>
            <Socials />
          </div>
          <div className={classes.legalLinks}>
            {/* Map through all links except the last one */}
            {legalLinks.fields.links
              ?.slice(0, -1)
              .map(({ fields: { text, url } }) => (
                <Fragment key={text}>
                  <Link
                    href={url}
                    className={classes.legalLink}
                    noPrefetchOnHover
                  >
                    {text}
                  </Link>
                  <span className={classes.legalLinksSlash}>/</span>
                </Fragment>
              ))}

            {/* Manage Cookies link at n-1 position */}
            <Link
              href='#'
              className={classes.legalLink}
              onClick={(e) => {
                e.preventDefault(); // Prevent default link behavior
                clearConsentCookiesAndReload(); // Clear cookies and reload the page
              }}
            >
              Manage Cookies
            </Link>
            <span className={classes.legalLinksSlash}>/</span>

            {/* Render the last legal link */}
            {legalLinks.fields.links
              ?.slice(-1)
              .map(({ fields: { text, url } }) => (
                <Fragment key={text}>
                  <Link
                    href={url}
                    className={classes.legalLink}
                    noPrefetchOnHover
                  >
                    {text}
                  </Link>
                </Fragment>
              ))}

            <div className={classes.privacyOptions}>
              <PrivacyOptions />
            </div>
          </div>
        </Grid2>
      </GridContainer2>
    </footer>
  );
}
